// OwnerLabel — a row's label, plus "· <project> only" when one project owns it
// (INF-8). Alex, 2026-09-25: internal people work "as if they are a project" —
// Standard or a specific project, chosen by the top-bar Working-in
// selector — so the same catalog holds shared rows and one project's own rows,
// and the grid has to say which is which.
//
// IN WORDS, NOT COLOUR — Alex is colour-blind. Standard rows print only their
// label: shared is the default and needs no marker.
//
// `onPromote` (internal only) adds "Promote to Standard". It is the fix for the
// working-in design's one trap: the selector remembers last week's project, so a
// row meant for everyone can land as one project's. The server refuses what it
// cannot promote (a product built from project-only rows) and says why; the
// message is shown right under this label, next to the button that caused it.
import { useState } from 'react';
import { WARN } from '../icons.js';

export default function OwnerLabel({ label, ownerOnly, onPromote }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!ownerOnly) return label ?? '';

  const promote = async (e) => {
    e.stopPropagation();                     // a click here is not a row selection
    setBusy(true); setError(null);
    try { await onPromote(); }
    catch (err) { setError(err.message || 'Promote failed'); }
    finally { setBusy(false); }
  };

  // The marker sits on its OWN line: inline, a 230px Label column clipped it to
  // "1001 onl…" and hid the button entirely (found in the browser, 2026-09-25).
  // The taller row is also a SHAPE difference — project rows stand out without
  // colour.
  return (
    <span>
      {label ?? ''}
      <span style={{ display: 'block', color: 'var(--th-text-muted)', fontSize: 'var(--fs-small)', whiteSpace: 'nowrap' }}>
        {ownerOnly} only
        {onPromote && (
          <>
            {' '}
            <button
              type="button"
              onClick={promote}
              disabled={busy}
              title={`Make this row Standard, visible to every project. There is no way back to ${ownerOnly} only.`}
              style={{
                fontSize: 'var(--fs-small)', padding: '0 6px', borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
                border: '1px solid var(--th-border-subtle)', background: 'var(--th-row-even)', color: 'var(--th-text-body)',
              }}
            >{busy ? 'Promoting…' : 'Promote to Standard'}</button>
          </>
        )}
      </span>
      {error && <span role="alert" style={{ display: 'block', fontSize: 'var(--fs-small)', color: 'var(--tg-warn-color)' }}>{WARN} {error}</span>}
    </span>
  );
}
